/**
 * Shared credit report import status helpers (dashboard + onboarding).
 */

import { AUTH_TOKEN_KEY } from './authUtils';
import apiFetchJson from '../services/apiClient';

export const IMPORT_SUCCESS_STATUSES = new Set(['completed', 'success']);
export const TERMINAL_FAILURE_STATUSES = new Set([
  'failed',
  'error',
  'cancelled',
]);
export const PROCESSING_STATUSES = new Set([
  'queued',
  'pending',
  'running',
  'processing',
]);
export const IMPORT_FAILED_STATUSES = new Set([
  ...TERMINAL_FAILURE_STATUSES,
  'unsupported_flow',
  'action_needed',
]);
export const IMPORT_TERMINAL_STATES = new Set([
  'success',
  'completed',
  'failed',
  'action_needed',
  'unsupported_flow',
  'cancelled',
  'browser_launch_failed',
  'not_found',
  'status_unavailable',
]);
export const IMPORT_OUTCOMES = {
  NEW_IMPORT_COMPLETED: 'new_import_completed',
  EXISTING_REPORT: 'existing_report',
  IMPORT_FAILED: 'import_failed',
  REVIEW_NEEDED: 'review_needed',
};
export const PROGRESS_CAP_INCOMPLETE = 95;
export const MAX_STATUS_POLL_FAILURES = 3;
/** Bounded retries for real 401/403 responses before surfacing the auth error. */
export const MAX_AUTH_POLL_FAILURES = 3;
export const SESSION_STALL_TIMEOUT_MS = 90000;
export const SESSION_STALL_MESSAGE =
  'The secure import session took too long to open. Please try again.';
export const SLOW_IMPORT_TIMEOUT_MS = 90000;
export const SLOW_IMPORT_MESSAGE =
  'Your report is taking longer than expected to process. You may keep this window open or return later.';
export const SLOW_IMPORT_POLL_INTERVAL_MS = 4000;
export const IMPORT_COMPLETED_MISSING_REPORT_MESSAGE =
  'Report saved but could not be opened. Please refresh your report list or retry the import.';

export const IMPORT_SERVICE_UNAVAILABLE_TITLE = 'Import Service Unavailable';
export const IMPORT_SERVICE_UNAVAILABLE_MESSAGE =
  'The secure import service is temporarily unavailable. Please try again in a few minutes.';

export const IMPORT_SESSION_EXPIRED_MESSAGE =
  'Your session expired. Please sign in again.';
export const IMPORT_ACCESS_DENIED_MESSAGE =
  'You do not have access to this import. Refresh the page or contact your administrator.';
export const IMPORT_STATUS_UPDATES_UNAVAILABLE_MESSAGE =
  'The import is still processing, but status updates are temporarily unavailable.';
export const IMPORT_PROCESSING_FAILED_MESSAGE =
  'The report import could not complete.';

export const IMPORT_ERROR_KINDS = {
  CREDENTIAL: 'credential',
  SERVICE_UNAVAILABLE: 'service_unavailable',
  BROWSER_LAUNCH: 'browser_launch',
  AUTHORIZATION: 'authorization',
  GENERIC: 'generic',
};

/** Authorization/session errors from status polling — NOT provider credential failures. */
export const AUTH_IMPORT_ERROR_CODES = new Set([
  'status_http_401',
  'status_http_403',
]);

/** Failure codes that indicate the user should review provider credentials. */
export const CREDENTIAL_IMPORT_ERROR_CODES = new Set([
  'invalid_credentials',
  'invalid_security_answer',
  'invalid_secret_answer',
  'credentials_required',
  'authentication_failed',
  'security_question_failed',
]);

/** Failure codes for backend/service availability — not credential issues. */
export const SERVICE_UNAVAILABLE_ERROR_CODES = new Set([
  'status_poll_failed',
  'status_http_502',
  'status_http_503',
  'status_http_504',
  'status_http_500',
  'service_unavailable',
  'status_unavailable',
  'provider_unreachable',
  'provider_unavailable',
  'not_found',
]);

export const BROWSER_LAUNCH_ERROR_CODES = new Set([
  'browser_launch_failed',
  'session_stalled',
]);

const normalizeErrorCode = (error) => {
  if (!error) return 'unknown_import_failure';
  if (typeof error === 'string') return error;
  return (
    error.code
    || error.failure_reason
    || error.details?.failure_reason
    || error.details?.state
    || 'unknown_import_failure'
  );
};

const normalizeErrorMessage = (error, fallback = 'Import failed. Please try again.') => {
  if (!error) return fallback;
  if (typeof error === 'string') return error;
  return error.message || error.details?.message || fallback;
};

/** Classify import errors for recovery UI (credential vs service vs browser). */
export const classifyImportError = (error) => {
  const code = String(normalizeErrorCode(error)).toLowerCase();
  const message = normalizeErrorMessage(error);

  if (CREDENTIAL_IMPORT_ERROR_CODES.has(code)) {
    return {
      kind: IMPORT_ERROR_KINDS.CREDENTIAL,
      code,
      title: 'Import Failed',
      message,
      showEditCredentials: true,
      showTryAgain: false,
    };
  }

  if (
    BROWSER_LAUNCH_ERROR_CODES.has(code)
    || code === 'browser_launch_failed'
    || message === SESSION_STALL_MESSAGE
  ) {
    return {
      kind: IMPORT_ERROR_KINDS.BROWSER_LAUNCH,
      code,
      title: 'Import Failed',
      message: message || SESSION_STALL_MESSAGE,
      showEditCredentials: false,
      showTryAgain: true,
    };
  }

  if (AUTH_IMPORT_ERROR_CODES.has(code)) {
    const isSessionExpired = code === 'status_http_401';
    return {
      kind: IMPORT_ERROR_KINDS.AUTHORIZATION,
      code,
      title: isSessionExpired ? 'Session Expired' : 'Access Restricted',
      message: isSessionExpired
        ? IMPORT_SESSION_EXPIRED_MESSAGE
        : IMPORT_ACCESS_DENIED_MESSAGE,
      showEditCredentials: false,
      showTryAgain: false,
    };
  }

  if (
    SERVICE_UNAVAILABLE_ERROR_CODES.has(code)
    || code.startsWith('status_http_')
  ) {
    return {
      kind: IMPORT_ERROR_KINDS.SERVICE_UNAVAILABLE,
      code,
      title: IMPORT_SERVICE_UNAVAILABLE_TITLE,
      message: code === 'status_poll_failed'
        ? IMPORT_STATUS_UPDATES_UNAVAILABLE_MESSAGE
        : IMPORT_SERVICE_UNAVAILABLE_MESSAGE,
      showEditCredentials: false,
      showTryAgain: true,
    };
  }

  return {
    kind: IMPORT_ERROR_KINDS.GENERIC,
    code,
    title: 'Import Failed',
    message,
    showEditCredentials: false,
    showTryAgain: true,
  };
};

export const isCredentialImportFailure = (error) =>
  classifyImportError(error).kind === IMPORT_ERROR_KINDS.CREDENTIAL;
const IMPORT_VERIFY_TIME_SKEW_MS = 3000;

export const getBackendRootUrl = () => {
  const raw = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
  return raw.replace(/\/api$/, '');
};

export const getApiBase = () => `${getBackendRootUrl()}/api`;

export const getImportStatusUrl = (importId) =>
  `${getApiBase()}/credit-report/import/${importId}/status`;

export const getImportWebSocketUrl = (importId) => {
  const root = getBackendRootUrl();
  return `${root.replace(/^http/, 'ws')}/ws/import/${importId}`;
};

export const mapImportStateToStatus = (state) => {
  if (!state) return 'pending';
  if (state === 'success' || state === 'completed') return 'completed';
  if (IMPORT_FAILED_STATUSES.has(state)) return 'failed';
  if (state === 'idle' || state === 'credentials_submitted') return 'pending';
  return 'running';
};

export const isImportProcessingStatus = (normalized) => {
  if (!normalized) return false;
  const status = String(normalized.status || mapImportStateToStatus(normalized.state)).toLowerCase();
  return PROCESSING_STATUSES.has(status);
};

export const isStaleImportStatusResponse = (normalized, activeImportId) => {
  if (!normalized || !activeImportId) return false;
  const responseImportId = normalized.import_id || normalized.import_job_id;
  return Boolean(responseImportId && responseImportId !== activeImportId);
};

export const normalizeImportStatusPayload = (data) => {
  if (!data || typeof data !== 'object') {
    return { status: 'pending', state: 'idle', progress: 0, message: 'Initializing...' };
  }
  const status = data.status || mapImportStateToStatus(data.state);
  const state = data.state === 'success' ? 'completed' : data.state;
  return { ...data, status, state };
};

export const canShowFullImportProgress = (normalized) => {
  return isImportReadyForVerification(normalized);
};

export const capImportProgress = (value, normalized, cap = PROGRESS_CAP_INCOMPLETE) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  if (canShowFullImportProgress(normalized)) {
    return Math.min(Math.max(numeric, 0), 100);
  }
  return Math.min(Math.max(numeric, 0), cap);
};

export const isImportTerminalFailure = (normalized) => {
  if (!normalized) return false;

  const status = String(normalized.status || '').toLowerCase();
  const state = String(normalized.state || '').toLowerCase();
  const hasExplicitFailure = Boolean(
    normalized.failure_reason
    || normalized.failure_message
    || normalized.error
  );

  if (isImportProcessingStatus(normalized) && !hasExplicitFailure) {
    return false;
  }

  if (TERMINAL_FAILURE_STATUSES.has(status)) return true;
  if (hasExplicitFailure) return true;
  if (state === 'browser_launch_failed') return true;
  if (state === 'not_found') return true;
  if (state === 'status_unavailable') return true;
  if (state === 'action_needed') return true;
  return IMPORT_TERMINAL_STATES.has(state) && state !== 'success' && state !== 'completed';
};

/** Terminal success from status API — still requires job-bound report verification before UI success. */
export const isImportReadyForVerification = (normalized) => {
  if (!normalized?.report_id) return false;
  if (normalized.report_saved === false) return false;
  if (isImportTerminalFailure(normalized)) return false;
  const backendCompleted =
    normalized.state === 'success'
    || normalized.state === 'completed'
    || IMPORT_SUCCESS_STATUSES.has(normalized.status);
  if (backendCompleted) return true;
  return Boolean(normalized.report_saved && normalized.report_id);
};

export const isImportTerminalSuccess = (normalized) => isImportReadyForVerification(normalized);

/** Whether a saved report artifact exists on a terminal import status payload. */
export const importHasSavedReport = (normalized) => {
  if (!normalized?.report_id) return false;
  if (normalized.report_saved === false) return false;
  const isCompleted =
    normalized.state === 'success'
    || normalized.state === 'completed'
    || IMPORT_SUCCESS_STATUSES.has(normalized.status);
  return isCompleted;
};

/**
 * Classify terminal import completion for UI routing.
 * Returns { kind: 'success' | 'review' | 'failed', outcome, code? }
 */
export const classifyImportCompletion = (normalized) => {
  if (!importHasSavedReport(normalized)) {
    if (!normalized?.report_id) {
      return { kind: 'failed', outcome: IMPORT_OUTCOMES.IMPORT_FAILED, code: 'missing_report_id' };
    }
    if (normalized.report_saved === false) {
      return { kind: 'failed', outcome: IMPORT_OUTCOMES.IMPORT_FAILED, code: 'report_not_saved' };
    }
    return { kind: 'failed', outcome: IMPORT_OUTCOMES.IMPORT_FAILED, code: 'not_completed' };
  }

  if (normalized.parser_success === false) {
    return { kind: 'review', outcome: IMPORT_OUTCOMES.REVIEW_NEEDED, code: 'parser_failed' };
  }

  return { kind: 'success', outcome: IMPORT_OUTCOMES.NEW_IMPORT_COMPLETED };
};

export const buildImportCompleteResult = (normalized, outcome = IMPORT_OUTCOMES.NEW_IMPORT_COMPLETED) => {
  const base = normalized?.result && typeof normalized.result === 'object'
    ? normalized.result
    : {};
  return {
    ...base,
    outcome,
    import_id: normalized?.import_id || normalized?.import_job_id,
    report_id: normalized?.report_id || base.report_id,
    imported_at: normalized?.imported_at,
    provider: normalized?.provider,
    scores: base.scores || normalized?.scores,
    total_accounts: base.total_accounts ?? normalized?.total_accounts,
  };
};

export const getImportFailureMessage = (normalized) =>
  normalized?.error ||
  normalized?.failure_message ||
  normalized?.message ||
  'Import failed. Please try again.';

/** True when import appears stuck opening the browser / early session. */
export const isImportSessionStalledProgress = (normalized) => {
  if (!normalized) return false;
  const state = String(normalized.state || '').toLowerCase();
  const message = String(normalized.message || '').toLowerCase();
  if (state === 'session_started') return true;
  if (message.includes('opening browser')) return true;
  return false;
};

const resolveAuthHeaders = (getAuthHeaders) => {
  const headers = getAuthHeaders?.() || {};
  const token = headers.Authorization
    || `Bearer ${localStorage.getItem('fundelite_token') || ''}`;
  return { ...headers, Authorization: token };
};

/**
 * Build request headers for import status polling.
 *
 * Matches import creation: Authorization bearer (when a token exists),
 * caller-provided scope headers (e.g. X-Agency-ID), never an empty bearer —
 * cookie-only sessions rely on credentials: 'include' instead.
 */
export const buildImportStatusRequestHeaders = ({ getAuthHeaders, agencyId } = {}) => {
  const headers = { Accept: 'application/json', ...(getAuthHeaders?.() || {}) };

  if (!headers.Authorization) {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }
  if (typeof headers.Authorization === 'string' && !headers.Authorization.replace(/^Bearer\s*/i, '')) {
    delete headers.Authorization;
  }
  if (agencyId && !headers['X-Agency-ID']) {
    headers['X-Agency-ID'] = agencyId;
  }
  return headers;
};

/**
 * Fetch import status once through the shared authenticated API client
 * (bearer token, session cookie via credentials: 'include', 401 refresh retry),
 * plus the same X-Agency-ID scope used when the import was created.
 *
 * Returns { ok, status, body } and never throws for HTTP errors so pollers can
 * make retry decisions without a try/catch per status code. Network errors
 * return { ok: false, status: 0, body: null }.
 */
export const fetchImportStatusOnce = async (importId, { getAuthHeaders, agencyId, signal } = {}) => {
  const headers = buildImportStatusRequestHeaders({ getAuthHeaders, agencyId });
  try {
    const body = await apiFetchJson(getImportStatusUrl(importId), {
      method: 'GET',
      headers,
      credentials: 'include',
      signal,
    });
    return { ok: true, status: 200, body };
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    return { ok: false, status: error?.status ?? 0, body: error?.data ?? null };
  }
};

/**
 * Pure check: does this report row belong to the active import job?
 */
export const reportMatchesActiveImport = (report, importId, importStartedAt) => {
  if (!report || !importId) {
    return { verified: false, reason: 'missing_report_or_import_id' };
  }

  const reportImportId = report.import_id || report.import_job_id;
  if (reportImportId && reportImportId === importId) {
    return { verified: true, reason: 'import_id_match' };
  }

  const reportTimestamp = report.imported_at || report.created_at;
  if (importStartedAt && reportTimestamp) {
    const startedMs = Date.parse(importStartedAt);
    const createdMs = Date.parse(reportTimestamp);
    if (
      !Number.isNaN(startedMs)
      && !Number.isNaN(createdMs)
      && createdMs >= startedMs - IMPORT_VERIFY_TIME_SKEW_MS
    ) {
      return { verified: true, reason: 'created_after_import_start' };
    }
    return { verified: false, reason: 'report_predates_import' };
  }

  return { verified: false, reason: 'cannot_link_report_to_import' };
};

/**
 * Fetch report-list and verify report_id belongs to the active import job.
 */
export const verifyReportBelongsToImport = async ({
  reportId,
  importId,
  importStartedAt,
  getAuthHeaders,
}) => {
  if (!reportId || !importId) {
    return { verified: false, reason: 'missing_ids', outcome: IMPORT_OUTCOMES.IMPORT_FAILED };
  }

  try {
    const headers = resolveAuthHeaders(getAuthHeaders);
    const response = await fetch(`${getApiBase()}/report-list`, { headers });

    if (!response.ok) {
      return { verified: false, reason: 'report_list_failed', outcome: IMPORT_OUTCOMES.IMPORT_FAILED };
    }

    const data = await response.json();
    const reports = data?.reports || [];
    const report = reports.find((entry) => entry.report_id === reportId);

    if (!report) {
      return { verified: false, reason: 'report_not_in_list', outcome: IMPORT_OUTCOMES.IMPORT_FAILED };
    }

    const match = reportMatchesActiveImport(report, importId, importStartedAt);
    if (match.verified) {
      return {
        ...match,
        outcome: IMPORT_OUTCOMES.NEW_IMPORT_COMPLETED,
        report,
      };
    }

    const outcome =
      match.reason === 'report_predates_import'
        ? IMPORT_OUTCOMES.EXISTING_REPORT
        : IMPORT_OUTCOMES.IMPORT_FAILED;

    return { ...match, outcome, report };
  } catch (error) {
    console.error('verifyReportBelongsToImport failed:', error);
    return { verified: false, reason: 'verification_error', outcome: IMPORT_OUTCOMES.IMPORT_FAILED };
  }
};
